import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Search, History, Settings, ExternalLink, Loader2, Users, LogOut, Crown, Clock, Key, ShieldAlert, FileText, HelpCircle, FileSignature } from "lucide-react";
import { useNavigate } from "react-router-dom";
import TermsAcceptanceModal from "@/components/TermsAcceptanceModal";

const SearchPage = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [query, setQuery] = useState("");
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedSites, setSelectedSites] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [subscription, setSubscription] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);

  // Check if user needs to accept terms
  useEffect(() => {
    if (user && !user.terms_accepted) {
      setShowTermsModal(true);
    }
  }, [user]);

  const handleTermsAccepted = () => {
    setShowTermsModal(false);
    // Refresh user data to update terms_accepted status
    try {
      if (typeof refreshUser === 'function') {
        refreshUser();
      }
      if (typeof toast?.success === 'function') {
        toast.success("Conditions acceptées. Bienvenue sur SNE!");
      }
    } catch (e) {
      console.error('Error in handleTermsAccepted:', e);
    }
  };

  useEffect(() => {
    fetchSites();
    fetchCategories();
    fetchSubscription();
  }, []);

  // Heartbeat to track online users
  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        await axios.post(`${API}/heartbeat`);
      } catch (error) {
        // Silent fail for heartbeat
      }
    };

    // Send heartbeat immediately and then every 5 minutes
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch online users count (admin only)
  useEffect(() => {
    if (user?.role !== 'admin') return;
    
    const fetchOnlineCount = async () => {
      try {
        const response = await axios.get(`${API}/admin/online-users`);
        setOnlineCount(response.data.online_count);
      } catch (error) {
        // Silent fail
      }
    };

    fetchOnlineCount();
    const interval = setInterval(fetchOnlineCount, 60 * 1000); // Update every minute
    return () => clearInterval(interval);
  }, [user?.role]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/check`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement de l'abonnement:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des catégories:", error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`);
      setSites(response.data);
      // Select all active sites by default
      const activeSites = response.data
        .filter(site => site.is_active)
        .map(site => site.id);
      setSelectedSites(new Set(activeSites));
    } catch (error) {
      toast.error("Erreur lors du chargement des sites");
    }
  };

  const getPlanDisplay = () => {
    if (!subscription) return null;
    
    const planNames = {
      'admin': 'Administrateur',
      'demo': 'Démo',
      'monthly': 'Mensuel',
      'annual': 'Annuel',
      'enterprise': 'Entreprise',
      'manual': 'Manuel',
      'promo': 'Promo',
      'test': 'Test'
    };
    
    const planColors = {
      'admin': 'text-purple-400 bg-purple-500/20',
      'demo': 'text-yellow-400 bg-yellow-500/20',
      'monthly': 'text-blue-400 bg-blue-500/20',
      'annual': 'text-green-400 bg-green-500/20',
      'enterprise': 'text-amber-400 bg-amber-500/20',
      'manual': 'text-gray-400 bg-gray-500/20',
      'promo': 'text-pink-400 bg-pink-500/20',
      'test': 'text-cyan-400 bg-cyan-500/20'
    };
    
    // Extract base plan from values like "demo-active", "demo_expired", "monthly-active"
    let basePlan = subscription.plan || '';
    if (basePlan.includes('-')) {
      basePlan = basePlan.split('-')[0];
    }
    if (basePlan.includes('_')) {
      basePlan = basePlan.split('_')[0];
    }
    
    const planName = planNames[basePlan] || basePlan || 'Standard';
    const colorClass = planColors[basePlan] || 'text-gray-400 bg-gray-500/20';
    
    // Calculate display info based on plan type
    const getSubscriptionInfo = () => {
      const days = subscription.days_remaining;
      const expiresAt = subscription.expires_at;
      
      // For demo plans: show days remaining
      if (basePlan === 'demo') {
        if (days === undefined || days === null) return null;
        if (days <= 0) {
          return { text: 'Dernière journée', color: 'text-red-400' };
        } else if (days === 1) {
          return { text: '1 jour restant', color: 'text-orange-400' };
        } else {
          return { text: `${days} jours restants`, color: 'text-yellow-400' };
        }
      }
      
      // For monthly/annual plans: show expiration date
      if (basePlan === 'monthly' || basePlan === 'annual') {
        if (expiresAt) {
          const expDate = new Date(expiresAt);
          const formattedDate = expDate.toLocaleDateString('fr-CA');
          if (days !== undefined && days <= 7) {
            return { text: `Expire le ${formattedDate}`, color: 'text-orange-400' };
          }
          return { text: `Expire le ${formattedDate}`, color: 'text-gray-400' };
        }
        return null;
      }
      
      // For other plans with days remaining
      if (days !== undefined && days !== null) {
        if (days <= 0) {
          return { text: 'Dernière journée', color: 'text-red-400' };
        } else if (days <= 3) {
          return { text: `${days} jour${days > 1 ? 's' : ''} restant${days > 1 ? 's' : ''}`, color: 'text-orange-400' };
        } else {
          return { text: `${days} jours restants`, color: 'text-gray-500' };
        }
      }
      
      return null;
    };
    
    const subInfo = getSubscriptionInfo();
    
    return (
      <div className="flex items-center gap-2">
        <span className={`w-[90px] h-[70px] rounded-2xl text-xs font-medium ${colorClass} flex flex-col items-center justify-center`}>
          <Crown className="w-5 h-5 mb-1" />
          {planName}
        </span>
        {basePlan === 'admin' && onlineCount > 0 && (
          <span className="w-[90px] h-[70px] rounded-2xl text-xs font-medium text-green-400 bg-green-500/20 flex flex-col items-center justify-center">
            <Users className="w-5 h-5 mb-1" />
            {onlineCount} en ligne
          </span>
        )}
        {subInfo && (
          <span className={`text-xs flex items-center gap-1 ${subInfo.color}`}>
            <Clock className="w-3 h-3" />
            {subInfo.text}
          </span>
        )}
      </div>
    );
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error("Veuillez entrer une requête de recherche");
      return;
    }

    if (selectedSites.size === 0) {
      toast.error("Veuillez sélectionner au moins un site");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/search`, {
        query: query,
        site_ids: Array.from(selectedSites)
      });
      setResults(response.data);
      toast.success(`Recherche effectuée sur ${response.data.results.length} sites`);
    } catch (error) {
      toast.error("Erreur lors de la recherche");
    } finally {
      setLoading(false);
    }
  };

  const toggleSite = (siteId) => {
    const newSelected = new Set(selectedSites);
    if (newSelected.has(siteId)) {
      newSelected.delete(siteId);
    } else {
      newSelected.add(siteId);
    }
    setSelectedSites(newSelected);
  };

  const toggleAllInCategory = (category) => {
    const categorySites = getFilteredSites(category);
    const allSelected = categorySites.every(site => selectedSites.has(site.id));
    
    const newSelected = new Set(selectedSites);
    categorySites.forEach(site => {
      if (allSelected) {
        newSelected.delete(site.id);
      } else {
        newSelected.add(site.id);
      }
    });
    setSelectedSites(newSelected);
  };

  const getFilteredSites = (category) => {
    if (category === "all") return sites.filter(s => s.is_active);
    // Find the category name by ID
    const cat = categories.find(c => c.id === category);
    const categoryName = cat ? cat.name : category;
    return sites.filter(s => s.is_active && s.category === categoryName);
  };

  const groupResultsByCategory = () => {
    if (!results) return {};
    
    const grouped = {};
    categories.forEach(cat => {
      grouped[cat.id] = [];
    });

    results.results.forEach(result => {
      const site = sites.find(s => s.id === result.site_id);
      if (site) {
        // Find the category ID by matching the site's category name
        const cat = categories.find(c => c.name === site.category);
        if (cat && grouped[cat.id] !== undefined) {
          grouped[cat.id].push(result);
        }
      }
    });

    return grouped;
  };

  const getCategoryLabel = (categoryId) => {
    if (categoryId === "all") return "Tous les sites";
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  const getCategoryShortLabel = (categoryId) => {
    if (categoryId === "all") return "Tous";
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return categoryId;
    // Return label if exists, otherwise format the name
    if (cat.label) return cat.label;
    // Format name: "search_engine" -> "Search engine", "social" -> "Social"
    return cat.name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
  };

  return (
    <div className="min-h-screen p-6">
      {/* Terms Acceptance Modal */}
      <TermsAcceptanceModal isOpen={showTermsModal} onAccept={handleTermsAccepted} />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8 fade-in">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-5xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              OSINT MultiSearch
            </h1>
            <p className="text-lg text-gray-400">Recherche simultanée sur plusieurs plateformes</p>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-gray-500">
                Connecté en tant que: <span className="text-blue-400">{user?.username}</span>
                {user?.account_type === 'promo' && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded bg-orange-500/20 text-orange-400">Promo</span>
                )}
                {user?.account_type === 'test' && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded bg-cyan-500/20 text-cyan-400">Test</span>
                )}
              </p>
              {getPlanDisplay()}
            </div>
          </div>
          <div className="flex gap-3 flex-wrap justify-end">
            {/* Guide des termes de recherche - Visible pour tous */}
            <Button
              data-testid="terms-guide-btn"
              onClick={() => window.open('/termes-recherche.pdf', '_blank')}
              variant="outline"
              className="bg-transparent border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400"
              title="Guide des sites et termes de recherche"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Guide de recherche
            </Button>
            <Button
              data-testid="history-nav-btn"
              onClick={() => navigate("/history")}
              variant="outline"
              className="bg-transparent border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400"
            >
              <History className="w-4 h-4 mr-2" />
              Historique
            </Button>
            {user?.role === 'admin' && (
              <>
                <Button
                  data-testid="sites-nav-btn"
                  onClick={() => navigate("/sites")}
                  variant="outline"
                  className="bg-transparent border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:border-purple-400"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Gérer les sites
                </Button>
                <Button
                  data-testid="users-nav-btn"
                  onClick={() => navigate("/users")}
                  variant="outline"
                  className="bg-transparent border-green-500/50 text-green-400 hover:bg-green-500/10 hover:border-green-400"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Utilisateurs
                </Button>
                <Button
                  data-testid="codes-nav-btn"
                  onClick={() => navigate("/activation-codes")}
                  variant="outline"
                  className="bg-transparent border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Codes
                </Button>
                <Button
                  data-testid="abuse-alerts-nav-btn"
                  onClick={() => navigate("/abuse-alerts")}
                  variant="outline"
                  className="bg-transparent border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-400"
                >
                  <ShieldAlert className="w-4 h-4 mr-2" />
                  Anti-Abus
                </Button>
                <Button
                  data-testid="nda-nav-btn"
                  onClick={() => navigate("/nda")}
                  variant="outline"
                  className="bg-transparent border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:border-purple-400"
                >
                  <FileSignature className="w-4 h-4 mr-2" />
                  NDA
                </Button>
              </>
            )}
            <Button
              data-testid="logout-btn"
              onClick={logout}
              variant="outline"
              className="bg-transparent border-gray-500/50 text-gray-400 hover:bg-gray-500/10 hover:border-gray-400"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="glass rounded-2xl p-6 mb-6 slide-in">
          <div className="flex gap-4">
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Entrez votre requête de recherche..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="flex-1 bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 h-14 text-lg"
            />
            <Button
              data-testid="search-btn"
              onClick={handleSearch}
              disabled={loading}
              className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Rechercher
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Sites Selection */}
        <div className="glass rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Sélectionner les sites</h2>
          <Tabs defaultValue="all" value={activeCategory} onValueChange={setActiveCategory}>
            <div className="overflow-x-auto pb-2 mb-2">
              <TabsList className="inline-flex bg-gray-900/50 gap-1 h-auto p-1 min-w-max">
                <TabsTrigger value="all" data-testid="tab-all" className="px-4 py-2 whitespace-nowrap">Tous</TabsTrigger>
                {categories.map((cat) => (
                  <TabsTrigger key={cat.id} value={cat.id} data-testid={`tab-${cat.id}`} className="px-4 py-2 whitespace-nowrap">
                    {getCategoryShortLabel(cat.id)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {["all", ...categories.map(c => c.id)].map((category) => (
              <TabsContent key={category} value={category} className="mt-0">
                <div className="mb-3 flex justify-between items-center">
                  <span className="text-sm text-gray-400">
                    {selectedSites.size} site(s) sélectionné(s)
                  </span>
                  <Button
                    data-testid={`toggle-all-${category}-btn`}
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAllInCategory(category)}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    {getFilteredSites(category).every(site => selectedSites.has(site.id))
                      ? "Tout désélectionner"
                      : "Tout sélectionner"}
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {getFilteredSites(category).map((site) => (
                    <div
                      key={site.id}
                      data-testid={`site-item-${site.id}`}
                      className="flex items-center justify-between p-4 rounded-lg bg-gray-900/30 border border-gray-800 hover:border-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {site.icon?.startsWith('http') ? (
                          <img src={site.icon} alt="" className="w-6 h-6 object-contain rounded" />
                        ) : (
                          <span className="text-2xl">{site.icon}</span>
                        )}
                        <span className="font-medium">{site.name}</span>
                      </div>
                      <Switch
                        data-testid={`site-toggle-${site.id}`}
                        checked={selectedSites.has(site.id)}
                        onCheckedChange={() => toggleSite(site.id)}
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="max-w-7xl mx-auto fade-in">
          <div className="glass rounded-2xl p-6">
            <h2 className="text-2xl font-bold mb-4">
              Résultats pour "{results.query}"
            </h2>
            
            <Tabs defaultValue={categories.length > 0 ? categories[0].id : "all"} className="w-full">
              <TabsList className="flex flex-wrap bg-gray-900/50 mb-4 gap-1 h-auto p-1">
                {categories.map((cat) => {
                  const grouped = groupResultsByCategory();
                  const count = grouped[cat.id] ? grouped[cat.id].length : 0;
                  return (
                    <TabsTrigger key={cat.id} value={cat.id} data-testid={`results-tab-${cat.id}`} className="flex-shrink-0" title={cat.name}>
                      {getCategoryShortLabel(cat.id)} ({count})
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {Object.entries(groupResultsByCategory()).map(([category, categoryResults]) => (
                <TabsContent key={category} value={category}>
                  <div className="space-y-3">
                    {categoryResults.length > 0 ? (
                      categoryResults.map((result, index) => (
                        <div
                          key={index}
                          data-testid={`result-item-${result.site_id}`}
                          className="p-4 rounded-lg bg-gray-900/30 border border-gray-800 hover:border-blue-500/50 transition-colors card-hover"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              {(() => {
                                const siteIcon = sites.find(s => s.id === result.site_id)?.icon;
                                return siteIcon?.startsWith('http') ? (
                                  <img src={siteIcon} alt="" className="w-6 h-6 object-contain rounded" />
                                ) : (
                                  <span className="text-2xl">{siteIcon}</span>
                                );
                              })()}
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg">{result.site_name}</h3>
                                <p className="text-sm text-gray-400 truncate">{result.url}</p>
                              </div>
                            </div>
                            <Button
                              data-testid={`open-result-${result.site_id}`}
                              onClick={() => window.open(result.url, "_blank")}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Ouvrir
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        Aucun résultat dans cette catégorie
                      </div>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;